import {
  ApiError,
  NETWORK_ERROR_MESSAGE,
  apiRequest,
  getAccessToken,
  handleUnauthorizedAccess,
} from '../api/client';
import { API_ENDPOINTS, withParams } from '../config/apis';
import { categoryService } from './categoryService';
import {
  fetchBasicStatistics,
  fetchGraphSplits,
  type GraphCategory,
  type SplitDistributionSplit,
} from './graphApi';

export type { SplitDistributionSplit } from './graphApi';

export type FinishStatus = 'FINISHED' | 'NEEDS_REVIEW' | 'DNF' | 'DNS';

export type ResultSplit = {
  splitId: number;
  splitName: string;
  guntimeElapsedTime?: string;
  chiptimeElapsedTime?: string;
  timingPointTime?: string;
  modified?: boolean;
  modifyReason?: string;
};

export type ResultItem = {
  participantId: number;
  categoryId: number;
  bibNumber: number;
  chipNumber: number;
  name: string;
  gender: string;
  category: string;
  ageGroup: string;
  group: string;
  splits: ResultSplit[];
  finishStatus?: FinishStatus;
};

export type ResultTableRow = {
  rowNumber: number;
  participantId: number;
  bibNumber: number;
  chipNumber: number;
  name: string;
  gender: string;
  category: string;
  ageGroup: string;
  group: string;
  startRecordTime: string;
  guntimeRecordTimesBySplitId: Record<string, string>;
  finishRecordTime: string;
  totalElapsedTime: string;
  splits: ResultSplit[];
  finishStatus?: FinishStatus;
};

export type ResultQueryParams = {
  page?: number;
  size?: number;
  categoryId?: number;
  keyword?: string;
  gender?: string;
  ageGroup?: string;
  group?: string;
  finishStatus?: FinishStatus;
  sort?: string;
};

export type ResultDownloadFormat = 'pdf' | 'xlsx' | 'csv';

export type ResultDownloadRequest = {
  format: ResultDownloadFormat;
  categoryId: number;
};

export type ResultDownloadResponse = {
  fileName: string;
  downloadUrl: string;
};

export type ResultFilterOptions = {
  genders: string[];
  ageGroups: string[];
  groups: string[];
};

export type ResultsData = {
  rows: ResultTableRow[];
  categories: GraphCategory[];
  filterOptions: ResultFilterOptions;
  timingPointSplits: SplitDistributionSplit[];
  totalCount: number;
  page: number;
  size: number;
};

type RawResultSplit = {
  splitId?: number | string;
  guntimeElapsedTime?: string;
  chiptimeElapsedTime?: string;
  timingPointTime?: string;
  modified?: boolean;
  modifyReason?: string;
};

type RawResultItem = {
  participantId?: number | string;
  categoryId?: number | string;
  bibNumber?: number | string;
  chipNumber?: number | string;
  name?: string;
  gender?: string;
  category?: string;
  ageGroup?: string;
  group?: string;
  splits?: RawResultSplit[];
  split?: RawResultSplit[];
  finishStatus?: FinishStatus;
};

type RawResultListResponse = {
  results?: RawResultItem[];
  totalCount?: number | string;
  page?: number | string;
  size?: number | string;
};

type ResultUpdateSplitRequest = {
  splitId: number;
  timingPointTime: string;
};

export type ResultUpdateRequest = {
  modifyReason: string;
  splits: ResultUpdateSplitRequest[];
  finishStatus?: Exclude<FinishStatus, 'NEEDS_REVIEW'>;
};

type RawResultUpdateResponse = {
  id?: number | string;
  participantId?: number | string;
  categoryId?: number | string;
  splits?: RawResultSplit[];
  finishStatus?: FinishStatus;
};

type RawResultNeedsReviewResponse = {
  hasNeedsReview?: boolean;
};

function toNumber(value: number | string | undefined) {
  return Number(value ?? 0);
}

function normalizeResultItem(item: RawResultItem): ResultItem {
  return {
    participantId: toNumber(item.participantId),
    categoryId: toNumber(item.categoryId),
    bibNumber: toNumber(item.bibNumber),
    chipNumber: toNumber(item.chipNumber),
    name: item.name ?? '',
    gender: item.gender ?? '',
    category: item.category ?? '',
    ageGroup: item.ageGroup ?? '',
    group: item.group ?? '',
    splits: (item.splits ?? item.split ?? []).map((split) => ({
      splitId: toNumber(split.splitId),
      splitName: '',
      guntimeElapsedTime: split.guntimeElapsedTime,
      chiptimeElapsedTime: split.chiptimeElapsedTime,
      timingPointTime: split.timingPointTime,
      modified: split.modified,
      modifyReason: split.modifyReason,
    })),
    finishStatus: item.finishStatus,
  };
}

function getGuntimeRecordTime(split?: ResultSplit) {
  return split?.guntimeElapsedTime ?? '';
}

function createSplitNameMap(splits: SplitDistributionSplit[]) {
  return new Map(splits.map((split) => [split.splitId, split.splitName]));
}

function toResultTableRow(
  result: ResultItem,
  splitNameMap: Map<number, string>,
  rowIndex: number
): ResultTableRow {
  const sortedSplits = result.splits
    .map((split) => ({
      ...split,
      splitName: splitNameMap.get(split.splitId) ?? split.splitName,
    }))
    .sort((first, second) => first.splitId - second.splitId);
  const startSplit = sortedSplits[0];
  const finishSplit = sortedSplits.at(-1);
  const middleSplits = sortedSplits.slice(1, Math.max(sortedSplits.length - 1, 1));
  const guntimeRecordTimesBySplitId = Object.fromEntries(
    middleSplits.map((split) => [String(split.splitId), getGuntimeRecordTime(split)])
  );

  return {
    rowNumber: rowIndex + 1,
    participantId: result.participantId,
    bibNumber: result.bibNumber,
    chipNumber: result.chipNumber,
    name: result.name,
    gender: result.gender,
    category: result.category,
    ageGroup: result.ageGroup,
    group: result.group,
    startRecordTime: getGuntimeRecordTime(startSplit),
    guntimeRecordTimesBySplitId,
    finishRecordTime: getGuntimeRecordTime(finishSplit),
    totalElapsedTime: finishSplit?.chiptimeElapsedTime ?? '',
    splits: sortedSplits,
    finishStatus: result.finishStatus,
  };
}

function toResultQuery(params: ResultQueryParams) {
  return {
    page: params.page,
    size: params.size,
    categoryId: params.categoryId,
    keyword: params.keyword?.trim(),
    gender: params.gender,
    ageGroup: params.ageGroup,
    group: params.group,
    finishStatus: params.finishStatus,
    sort: params.sort,
  };
}

function uniqueSortedValues(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value)))).sort(
    (firstValue, secondValue) => firstValue.localeCompare(secondValue, 'ko')
  );
}

async function resolveAuxiliaryData<T>(request: Promise<T>, fallback: T) {
  try {
    return await request;
  } catch {
    return fallback;
  }
}

function createFallbackCategories(results: ResultItem[]): GraphCategory[] {
  const categories = new Map<number, string>();

  results.forEach((result) => {
    if (result.categoryId > 0 && result.category) {
      categories.set(result.categoryId, result.category);
    }
  });

  return Array.from(categories, ([id, categoryName]) => ({ id, categoryName }));
}

function createFallbackSplits(results: ResultItem[]): SplitDistributionSplit[] {
  const splitIds = new Set<number>();

  results.forEach((result) => {
    result.splits.forEach((split) => {
      if (split.splitId > 0) {
        splitIds.add(split.splitId);
      }
    });
  });

  return Array.from(splitIds)
    .sort((first, second) => first - second)
    .map((splitId) => ({
      splitId,
      splitName: `Split ${splitId}`,
    }));
}

function uniqueSplitsById(splits: SplitDistributionSplit[]) {
  const splitMap = new Map<number, SplitDistributionSplit>();

  splits.forEach((split) => {
    if (!splitMap.has(split.splitId)) {
      splitMap.set(split.splitId, split);
    }
  });

  return Array.from(splitMap.values());
}

function createTimingPointSplits(splits: SplitDistributionSplit[], rows: ResultTableRow[]) {
  if (splits.length === 0) {
    return uniqueSplitsById(rows.flatMap((row) => row.splits.slice(1, -1)));
  }

  const splitGroups = new Map<string, SplitDistributionSplit[]>();

  splits.forEach((split) => {
    const groupKey = split.categoryName ?? '__default__';
    splitGroups.set(groupKey, [...(splitGroups.get(groupKey) ?? []), split]);
  });

  return uniqueSplitsById(
    Array.from(splitGroups.values()).flatMap((splitGroup) => splitGroup.slice(1, -1))
  );
}

export async function fetchResults(
  eventId: number,
  params: ResultQueryParams = {}
): Promise<ResultsData> {
  const [resultsResponse, categoriesWithGroups, basicStatistics, auxiliarySplits] = await Promise.all([
    apiRequest<RawResultListResponse>(
      withParams(API_ENDPOINTS.resultList, { eventId }),
      { query: toResultQuery(params) }
    ),
    resolveAuxiliaryData(categoryService.list(eventId), []),
    resolveAuxiliaryData(fetchBasicStatistics(eventId), { ages: [], charts: [], totalParticipants: 0 }),
    resolveAuxiliaryData(fetchGraphSplits(eventId, params.categoryId), []),
  ]);
  const page = toNumber(resultsResponse.page);
  const size = toNumber(resultsResponse.size ?? params.size);
  const normalizedResults = (resultsResponse.results ?? []).map(normalizeResultItem);
  const splits = auxiliarySplits.length > 0 ? auxiliarySplits : createFallbackSplits(normalizedResults);
  const splitNameMap = createSplitNameMap(splits);
  const rows = normalizedResults.map((result, index) => toResultTableRow(result, splitNameMap, page * size + index));
  const categories = categoriesWithGroups.length > 0
    ? categoriesWithGroups.map((category) => ({
        id: category.id,
        categoryName: category.categoryName,
      }))
    : createFallbackCategories(normalizedResults);
  const filterOptions = {
    genders: uniqueSortedValues([
      ...basicStatistics.ages.map((age) => age.gender),
      ...normalizedResults.map((result) => result.gender),
    ]),
    ageGroups: uniqueSortedValues([
      ...basicStatistics.ages.map((age) => age.ageGroup),
      ...normalizedResults.map((result) => result.ageGroup),
    ]),
    groups: uniqueSortedValues([
      ...categoriesWithGroups.flatMap((category) => category.groups.map((group) => group.groupName)),
      ...normalizedResults.map((result) => result.group),
    ]),
  };
  const timingPointSplits = createTimingPointSplits(splits, rows);

  return {
    rows,
    categories,
    filterOptions,
    timingPointSplits,
    totalCount: toNumber(resultsResponse.totalCount ?? rows.length),
    page,
    size,
  };
}

export async function downloadResults(
  eventId: number,
  request: ResultDownloadRequest
) {
  return apiRequest<ResultDownloadResponse, ResultDownloadRequest>(
    withParams(API_ENDPOINTS.resultDownload, { eventId }),
    { body: request }
  );
}

export async function fetchResultDownloadFile(downloadUrl: string) {
  const headers = new Headers();
  const token = getAccessToken();

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let response: Response;
  try {
    response = await fetch(downloadUrl, {
      headers,
    });
  } catch {
    throw new ApiError(NETWORK_ERROR_MESSAGE, 0);
  }

  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorizedAccess();
    }
    throw new ApiError('결과 파일 다운로드 권한이 없거나 다운로드 주소가 만료되었습니다.', response.status);
  }

  return response.blob();
}

export async function updateResult(
  eventId: number,
  participantId: number,
  request: ResultUpdateRequest
) {
  return apiRequest<RawResultUpdateResponse, ResultUpdateRequest>(
    withParams(API_ENDPOINTS.resultUpdate, { eventId, participantId }),
    { body: request }
  );
}

export async function fetchHasNeedsReviewResult(eventId: number): Promise<boolean> {
  const response = await apiRequest<RawResultNeedsReviewResponse>(
    withParams(API_ENDPOINTS.resultNeedsReview, { eventId })
  );
  return response.hasNeedsReview ?? false;
}
